import { motion } from "framer-motion";
import { useTilt } from "../hooks/useTilt";

function TiltCard({ children, className, ...props }) {
  const { handlers, style, shineStyle } = useTilt(4);
  return (
    <div {...handlers} style={{ ...style, transformStyle: "preserve-3d" }} className={className} {...props}>
      <div style={shineStyle} />
      {children}
    </div>
  );
}

const BOWLING_IMG = "/images/neu/Pirate-Bowling-6.jpg";
const BAR_IMG = "/images/neu/Pirate-Bowling-11.jpg";
const DARTS_IMG = "/images/neu/Pirate-Bowling-17.jpg";

const AnchorIcon = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="5" r="2"/>
    <path d="M12 7v14M5 11a7 7 0 0 0 14 0"/>
    <line x1="5" y1="11" x2="2" y2="11"/>
    <line x1="22" y1="11" x2="19" y2="11"/>
  </svg>
);

const items = [
  {
    key: "bowling",
    title: "Bowling",
    subtitle: "Die Hauptattraktion",
    desc: "Mehrere professionelle Bahnen. Strik nach Strik – das perfekte Spiel für Gruppen jeder Größe.",
    tags: ["Gruppen", "Turniere", "Laser-Bowling"],
    img: BOWLING_IMG,
    span: "lg:col-span-2 lg:row-span-2",
    size: "min-h-[280px] sm:min-h-[350px] md:min-h-[420px] lg:min-h-[480px]",
    testId: "card-bowling",
  },
  {
    key: "billard",
    title: "Billard",
    subtitle: "Eleganz & Präzision",
    desc: "Professionelle Tische für stilvolle Spielabende.",
    img: "/images/neu/Pirate-Bowling-5.jpg",
    span: "",
    size: "min-h-[140px] sm:min-h-[160px] md:min-h-[180px]",
    accent: "border-gold/20 hover:border-gold/280",
    testId: "card-billiards",
  },
  {
    key: "arcade",
    title: "Arcade & Spiele",
    subtitle: "Retro trifft Abenteuer",
    desc: "Automaten, Dart und mehr – der perfekte Spielplatz für Groß und Klein.",
    img: DARTS_IMG,
    span: "",
    size: "min-h-[140px] sm:min-h-[160px] md:min-h-[180px]",
    accent: "border-gold/20 hover:border-gold/280",
    testId: "card-arcade",
  },
  {
    key: "kicker",
    title: "Tischkicker",
    subtitle: "Schnelle Runden",
    desc: "Emotionen pur – packendes Kickerduell zwischen Freunden.",
    img: "/images/neu/Pirate-Bowling-19.jpg",
    span: "",
    size: "min-h-[140px] sm:min-h-[160px] md:min-h-[180px]",
    accent: "border-gold/20 hover:border-gold/280",
    testId: "card-foosball",
  },
  {
    key: "bar",
    title: "Bar & Cocktails",
    subtitle: "Getränke für jeden Geschmack",
    desc: "Frisch gemixte Cocktails und kalte Getränke für den perfekten Abend.",
    img: BAR_IMG,
    span: "",
    size: "min-h-[140px] sm:min-h-[160px] md:min-h-[180px]",
    accent: "border-gold/20 hover:border-gold/280",
    testId: "card-bar",
  },
  {
    key: "airhockey",
    title: "Airhockey",
    subtitle: "Blitzschnell & spannend",
    desc: "Schnelle Duelle am Airhockey-Tisch – wer hat die besten Reflexe?",
    img: "/images/neu/Pirate-Bowling-16.jpg",
    span: "",
    size: "min-h-[140px] sm:min-h-[160px] md:min-h-[180px]",
    accent: "border-gold/20 hover:border-gold/280",
    testId: "card-airhockey",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.09, duration: 0.6, ease: "easeOut" } }),
};

export default function Entertainment() {
  return (
    <section
      id="entertainment"
      className="relative py-28 md:py-36 px-6 md:px-12 lg:px-20 bg-navy"
      data-testid="entertainment-section"
    >
      {/* Ambient */}
      <div className="hidden 2xl:block absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-gold/8 blur-[160px] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="skull-divider mb-6">
            <span className="font-space-grotesk text-xs text-gold tracking-[0.3em] uppercase px-3">Das Erlebnis</span>
          </div>
          <h2 className="font-unbounded text-4xl sm:text-5xl lg:text-6xl font-black text-cream leading-tight text-center">
            Mehr als nur<br/>
            <span className="gold-gradient-text">Bowling</span>
          </h2>
          <p className="font-space-grotesk text-cream-muted text-sm md:text-base mt-5 max-w-lg mx-auto text-center leading-relaxed">
            Sechs Welten der Unterhaltung unter einem Dach. Gemeinsam spielen, lachen, feiern.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-auto">
          {items.map((item, i) => (
            <motion.div
              key={item.key}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className={`${item.span}`}
              data-testid={item.testId}
            >
              <TiltCard className={`${item.size} relative overflow-hidden rounded-2xl glass border border-white/5 ${item.accent || ""} group card-hover cursor-pointer h-full`}>
                {/* Background Image */}
                {item.img && (
                  <>
                    <img
                      src={item.img}
                      alt={item.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 opacity-50 group-hover:opacity-60"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/95 via-navy/50 to-transparent" />
                  </>
                )}
                {!item.img && (
                  <div className="absolute inset-0 bg-gradient-to-br from-navy-light/50 to-navy-deep" />
                )}

                {/* Content */}
                <div className="relative z-10 p-6 md:p-8 h-full flex flex-col justify-end">
                  {item.key === "bowling" && (
                    <div className="flex items-center gap-2 mb-4">
                      <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
                      <span className="font-space-grotesk text-xs text-gold tracking-widest uppercase">{item.subtitle}</span>
                    </div>
                  )}
                  <h3 className={`font-unbounded font-black text-cream ${item.key === "bowling" ? "text-2xl sm:text-3xl md:text-4xl mb-3" : "text-lg sm:text-xl mb-2"}`}>
                    {item.title}
                  </h3>
                  <p className="font-inter text-cream/80 text-sm leading-relaxed">{item.desc}</p>
                  {item.tags && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {item.tags.map((tag) => (
                        <span key={tag} className="font-space-grotesk text-xs px-3 py-1 rounded-full bg-gold/10 border border-gold/20 text-gold">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Gold corner accent */}
                <div className="absolute top-0 right-0 w-16 h-16 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="absolute top-0 right-0 w-full h-full border-t-2 border-r-2 border-gold/40 rounded-tr-2xl" />
                </div>
              </TiltCard>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
